import {
  Alert,
  Box,
  Button,
  Icons,
  iconSize,
  LanguageToggle,
  palette,
  ProfilePhotoField,
  PronounsField,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../providers/AuthProvider';
import { useScope } from '../providers/ScopeProvider';
import { ProfileRoles } from '../components/settings/ProfileRoles';
import { PasskeysSection } from '../components/settings/PasskeysSection';
import { SettingsSection as Section } from '../components/settings/SettingsSection';
import { NotificationsSection } from '../components/settings/NotificationsSection';
import { WorkSummarySection } from '../components/settings/WorkSummarySection';
import { CalendarFeedSection } from '../components/settings/CalendarFeedSection';
import { CertificationsSection } from '../components/settings/CertificationsSection';
import { SnippetsSection } from '../components/settings/SnippetsSection';

const LOCALE_KEY = 'gov.locale';

/** One label/value row inside the profile card. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{
        py: 1.25,
        alignItems: { sm: 'center' },
        borderBottom: `1px solid ${palette.border.line}`,
        '&:first-of-type': { pt: 0 },
        '&:last-of-type': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Typography
        sx={{ fontSize: 13, color: palette.text.muted, width: { sm: 150 }, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, fontSize: 15, color: palette.text.primary }}>{value}</Box>
    </Stack>
  );
}

export function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, requestPasswordReset, uploadAvatar, removeAvatar, setPronouns } = useAuth();
  const { scopes } = useScope();
  const lang = (i18n.language as 'en' | 'fr') ?? 'en';

  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const onLang = (next: 'en' | 'fr') => {
    void i18n.changeLanguage(next);
    localStorage.setItem(LOCALE_KEY, next);
  };

  // There is no authenticated change-password endpoint; the honest option is the
  // same emailed reset link the sign-in screen uses.
  const onRequestReset = async () => {
    if (!user?.email) return;
    setResetState('sending');
    try {
      await requestPasswordReset(user.email);
      setResetState('sent');
    } catch {
      setResetState('error');
    }
  };

  const onSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <AppShell>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ display: { md: 'none' }, alignItems: 'center' }}>
          <Icons.SlidersHorizontal size={iconSize.lg} />
          <Typography variant="h5">{t('gov.settings.title')}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('gov.settings.subtitle')}
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        <Section
          icon={Icons.User}
          title={t('gov.settings.profile.title')}
          description={t('gov.settings.profile.description')}
        >
          <Box sx={{ mb: 2.5 }}>
            <ProfilePhotoField
              name={user?.displayName || ''}
              avatarUrl={user?.avatarUrl}
              onUpload={uploadAvatar}
              onRemove={removeAvatar}
              labels={{
                title: t('gov.settings.profile.photo.title'),
                description: t('gov.settings.profile.photo.description'),
                upload: t('gov.settings.profile.photo.upload'),
                replace: t('gov.settings.profile.photo.replace'),
                remove: t('gov.settings.profile.photo.remove'),
                invalidType: t('gov.settings.profile.photo.invalidType'),
                tooLarge: t('gov.settings.profile.photo.tooLarge'),
                failed: t('gov.settings.profile.photo.failed'),
              }}
            />
          </Box>
          <Box sx={{ mb: 2.5 }}>
            <PronounsField
              value={user?.pronouns ?? ''}
              onSave={setPronouns}
              labels={{
                title: t('gov.settings.profile.pronouns.title'),
                description: t('gov.settings.profile.pronouns.description'),
                placeholder: t('gov.settings.profile.pronouns.placeholder'),
                save: t('common.actions.save'),
                failed: t('gov.settings.profile.pronouns.failed'),
              }}
            />
          </Box>
          <Box>
            <Field label={t('gov.settings.profile.name')} value={user?.displayName || '—'} />
            <Field label={t('common.labels.email')} value={user?.email || '—'} />
            <Field
              label={t('gov.settings.profile.roles')}
              value={<ProfileRoles scopes={scopes} accountRoles={user?.roles} />}
            />
          </Box>
          <Typography sx={{ mt: 2, fontSize: 12, color: palette.text.muted }}>
            {t('gov.settings.profile.managedHint')}
          </Typography>
        </Section>

        <Section
          icon={Icons.Globe}
          title={t('common.labels.language')}
          description={t('gov.settings.language.description')}
        >
          <LanguageToggle value={lang} onChange={onLang} />
        </Section>

        <CertificationsSection />

        <NotificationsSection />

        <SnippetsSection />
        <WorkSummarySection />

        <CalendarFeedSection />

        <Section
          icon={Icons.Lock}
          title={t('gov.settings.security.title')}
          description={t('gov.settings.security.description')}
        >
          {resetState === 'sent' ? (
            <Alert severity="success">
              {t('gov.settings.security.sent', { email: user?.email ?? '' })}
            </Alert>
          ) : (
            <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              {resetState === 'error' && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {t('gov.settings.security.error')}
                </Alert>
              )}
              <Button
                variant="outlined"
                startIcon={<Icons.Mail size={iconSize.md} />}
                disabled={resetState === 'sending' || !user?.email}
                onClick={() => void onRequestReset()}
              >
                {resetState === 'sending'
                  ? t('gov.settings.security.sending')
                  : t('gov.settings.security.action')}
              </Button>
            </Stack>
          )}
        </Section>

        <PasskeysSection />

        <Section
          icon={Icons.LogOut}
          title={t('gov.settings.session.title')}
          description={t('gov.settings.session.description')}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<Icons.LogOut size={iconSize.md} />}
            onClick={() => void onSignOut()}
          >
            {t('common.actions.signOut')}
          </Button>
        </Section>
      </Stack>
    </AppShell>
  );
}
